// How a Section's individual cameras are identified, labelled and drawn against the aggregate they
// contribute to. Shared by the weight detail modal and the Productivity Chart, so a camera's ordinal,
// label and colour are one answer wherever one camera list is drawn - within ONE read only, since two
// surfaces resolve their rosters over different windows and need not agree on order.

import { withAlpha } from '@/plugins/highcharts/chartColors';
import { i18n } from '@/plugins/internationalization/i18n';
import colors from '@/plugins/tailwind';
import type { CameraSeries } from '@/types/SectionLiveKpi';
import { cameraOrdinal, cameraLabel } from '../production-groups/cameraIdentity';
import type { MetricKey } from './productivityMetrics';

export { cameraOrdinal, cameraLabel };

/**
 * A camera's colour, by ordinal. Wraps past the palette's length rather than running out: two cameras
 * sharing a hue is a legibility problem, a camera with no colour is a broken chart.
 */
export function cameraColor(index: number): string {
    const palette = colors.chart.cameras;
    return palette[index % palette.length];
}

/**
 * How see-through a camera line is drawn: low enough that six read as a band the aggregate sits inside,
 * high enough to trace one across the plot. A colour value, not series opacity, which would fade the
 * legend swatch with it.
 */
const CAMERA_LINE_OPACITY = 0.6;

/**
 * The strokes separating an aggregate from the cameras under it: wide solid on top, narrow dashed
 * behind, so the hierarchy survives greyscale and colour vision deficiency. Colour distinguishes one
 * camera from another and never a camera from the aggregate.
 */
const AGGREGATE_LINE_WIDTH = 3;
const CAMERA_LINE_WIDTH = 1;

/**
 * The stroke a camera takes once it is the only one left on the plot: wider than the band's hairline,
 * still narrower than the aggregate's, which is the primary line either way.
 */
const ISOLATED_CAMERA_LINE_WIDTH = 2;

/**
 * The bucket fields a camera states a figure in - the three weight figures, which is every figure a
 * camera reports. Water and the dead count are Section facts, so no camera bucket carries them.
 *
 * Both shapes a camera's buckets arrive in state exactly these three, which is what lets one field name
 * index either.
 */
export type CameraBucketField = 'weight_kg' | 'daily_gain_g' | 'growth_index';

/**
 * The two things true of a metric per camera: the dash its line takes - which carries which FIGURE a line
 * is, on a chart where colour is already spent on the camera - and the bucket field its figure is read
 * from, so narrowing a metric to one a camera reports (see isCameraMetric) settles both at once.
 *
 * Keyed by MetricKey, so a rename in the Productivity Chart's registry fails to compile rather than
 * silently drawing no camera curves.
 */
const CAMERA_METRICS = {
    weight: { dash: 'ShortDash', field: 'weight_kg' },
    dailyGain: { dash: 'ShortDot', field: 'daily_gain_g' },
    growthIndex: { dash: 'ShortDash', field: 'growth_index' },
} as const satisfies Partial<Record<MetricKey, { dash: string; field: CameraBucketField }>>;

export type CameraMetric = keyof typeof CAMERA_METRICS;

/** Which of a camera's three figures this metric is read from. */
export function cameraBucketField(metric: CameraMetric): CameraBucketField {
    return CAMERA_METRICS[metric].field;
}

/**
 * Whether a metric is one a camera reports at all. A type guard, so `filter(isCameraMetric)` both selects
 * the three and narrows to them.
 */
export function isCameraMetric(key: string): key is CameraMetric {
    return Object.prototype.hasOwnProperty.call(CAMERA_METRICS, key);
}

/**
 * Whether a Section's cameras are drawn as series of their own at all. False for a single-camera Section,
 * where the aggregate IS that camera.
 */
export function drawsCameraSeries(cameras: readonly CameraSeries<unknown>[]): boolean {
    return cameras.length > 1;
}

/**
 * What makes the aggregate read as the primary line, applied only where camera lines are drawn behind
 * it. Empty on a single-camera Section: there is nothing to be emphasised against, so it keeps its
 * ordinary stroke.
 */
export function aggregateEmphasis(multiCamera: boolean): { lineWidth?: number; zIndex?: number } {
    return multiCamera ? { lineWidth: AGGREGATE_LINE_WIDTH, zIndex: 3 } : {};
}

/**
 * Which of the two things a camera line is drawn as: `band` is one of several lines reading as a spread
 * around the aggregate, `isolated` is a camera alone on the plot. Named rather than a boolean, which says
 * nothing at the call site about which state it selects.
 */
export type CameraEmphasis = 'band' | 'isolated';

/**
 * How the cameras still on the plot should be drawn, given how many there are: hide all but one and the
 * survivor is no longer a spread the aggregate sits inside. Taken from the count rather than a per-camera
 * flag, because "is this camera on its own" is a fact about the set.
 */
export function cameraEmphasis(visibleCount: number): CameraEmphasis {
    return visibleCount === 1 ? 'isolated' : 'band';
}

/**
 * Everything a camera's line looks like: its own hue, a dashed stroke, a z-order behind the aggregate, and
 * a thickening on hover. The band's transparency is what makes several cameras read as a spread rather
 * than competing signals, and also what makes one hard to trace - so an isolated camera drops it.
 */
export function cameraSeriesStyle(index: number, metric: CameraMetric, emphasis: CameraEmphasis) {
    const isolated = emphasis === 'isolated';
    return {
        color: isolated ? cameraColor(index) : withAlpha(cameraColor(index), CAMERA_LINE_OPACITY),
        lineWidth: isolated ? ISOLATED_CAMERA_LINE_WIDTH : CAMERA_LINE_WIDTH,
        dashStyle: CAMERA_METRICS[metric].dash,
        zIndex: isolated ? 2 : 1,
        marker: { enabled: false },
        states: { hover: { lineWidthPlus: 1 } },
    };
}

/**
 * Which cameras the reader has switched off, as Sensor ids the caller can trust - sorted, de-duplicated,
 * and with any id no camera on this Section carries dropped, so a link made elsewhere half-lands rather
 * than refusing to draw.
 *
 * Sensor ids and not ordinals: an ordinal is a position in a roster ordered by mount date, so widening the
 * range renumbers everything after an earlier mount - and a choice travelling in the URL outlives the
 * window it was made in.
 */
export function resolveHiddenCameras(sensorIds: readonly number[], cameras: readonly CameraSeries<unknown>[]): number[] {
    // Nothing to hide on a Section drawing no camera curves: the aggregate is that one camera
    // already, and hiding it would leave the reader looking at a line they cannot switch back on.
    if (!drawsCameraSeries(cameras)) return [];
    const present = new Set(cameras.map((camera) => camera.sensor_id));
    return [...new Set(sensorIds.filter((sensorId) => present.has(sensorId)))].sort((left, right) => left - right);
}

/**
 * The hidden set with one camera flipped: added if it is on the plot, removed if it is off. Takes the
 * resolved set, so flipping also drops any Sensor id naming a camera this Section does not have.
 */
export function toggleHiddenCamera(hidden: readonly number[], sensorId: number): number[] {
    return hidden.includes(sensorId) ? hidden.filter((candidate) => candidate !== sensorId) : [...hidden, sensorId];
}

/**
 * A camera series' name as the legend shows it: the metric it plots, then the camera. Metric first
 * because a two-metric chart groups by metric in the legend's reading order.
 */
export function cameraSeriesName(metricLabel: string, camera: Pick<CameraSeries<unknown>, 'name'>, index: number): string {
    return i18n.global.t('views.monitoring.activeGroups.texts.cameraSeriesName', { metric: metricLabel, camera: cameraLabel(camera, index) }) as string;
}

/**
 * The series id one camera's curve is drawn under: the id of the series it stands beneath, then the
 * camera's ordinal. `baseId` is whatever identifies that series on the surface in question, so one shape of
 * id serves the modal, the Productivity Chart and a comparison halo alike.
 *
 * Ordinal, because that is the only key two camera lists share - a comparison reference is often a
 * different Section with different physical cameras.
 */
export function cameraSeriesKey(baseId: string, index: number): string {
    return `${baseId}:camera:${cameraOrdinal(index)}`;
}
